import { createElement, useCallback, useContext } from 'react'
import ModalContext from 'contexts/ModalContext'
import useHashSignal from 'hooks/useHashSignal'
import { useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import getIsLoyaltyEsimEnabled from 'loyalty/selectors/getIsLoyaltyEsimEnabled'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import LoyaltyEsimDetailsModal from 'loyalty/components/LoyaltyEsimDetailsModal'
import LoyaltyEsimBenefitsInfoModal from 'loyalty/components/LoyaltyEsimBenefitsInfoModal'

const ESIM_DETAILS_HASH = '#esim-details' as const
const ESIM_INFO_HASH = '#esim-info' as const

interface UseEsimHashModalOptions {
  orderId?: string
}

function useEsimHashModal(options?: UseEsimHashModalOptions) {
  const showModal = useContext(ModalContext)
  const loggedIn = useAppSelector(selectLoggedIn)
  const isEsimEnabled = useAppSelector(getIsLoyaltyEsimEnabled)
  const orderId = options?.orderId

  const openDetailsModal = useCallback(() => {
    if (!isEsimEnabled) return
    const modalElement = createElement(LoyaltyEsimDetailsModal, orderId ? { orderId } : undefined)
    if (loggedIn) {
      showModal(modalElement)
    } else {
      showModal<AccountAccessModalResult>(
        createElement(AccountAccessModal, { initialMode: 'login' }),
      ).then(result => {
        if (result?.loggedIn) {
          showModal(modalElement)
        }
      })
    }
  }, [isEsimEnabled, loggedIn, showModal, orderId])

  const openInfoModal = useCallback(() => {
    if (!isEsimEnabled) return
    if (loggedIn) {
      showModal(createElement(LoyaltyEsimBenefitsInfoModal))
    } else {
      showModal<AccountAccessModalResult>(
        createElement(AccountAccessModal, { initialMode: 'login' }),
      ).then(result => {
        if (result?.loggedIn) {
          showModal(createElement(LoyaltyEsimBenefitsInfoModal))
        }
      })
    }
  }, [isEsimEnabled, loggedIn, showModal])

  useHashSignal({
    hash: ESIM_DETAILS_HASH,
    onActivate: openDetailsModal,
  })

  useHashSignal({
    hash: ESIM_INFO_HASH,
    onActivate: openInfoModal,
  })
}

export default useEsimHashModal
